import AlertPopup from "components/alert-popup/AlertPopup";
import Button from "core/buttons/button/Button";
import ClientPersonalInfoForm from "components/form/client/ClientPersonalInfoForm";
import InfoContext from "pages/InfoContext";
import ValidationModal from "components/modal/ValidationModal";
import { CLIENT_FORM_DEFAULT_VALUES } from "constants/forms";
import { FIELD_REQUIRED } from "constants/popup-codes";
import { FormattedMessage } from "react-intl";
import { QUERY_STATUS, POPUP_STATUS, TAG_TYPES } from "constants/constants";
import { formatSentData } from "utils/clientHelper";
import { useBranchContext } from "api/settings/branch/BranchProviders";
import { useClientContext } from "api/directory/client/ClientProvider";
import { useEffect, useMemo, useRef, useState } from "react";
import { useFileContext } from "api/files/FilesProvider";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useSectorContext } from "api/settings/sector/SectorProvider";
import { useUserContext } from "api/user/UserProvider";
import { useProfileUploadSetup } from "utils/customHooks";
import colors from "assets/styles/mixins/colors.scss";
import { useSettingsContext } from "api/settings/SettingsProvider";

const CreateClient = () => {
  const {
    control,
    formState: { errors },
    handleSubmit,
    register,
    setValue,
    watch,
    clearErrors,
    reset,
    setError,
  } = useForm({
    defaultValues: CLIENT_FORM_DEFAULT_VALUES,
  });
  const { sectorListGetter, sectorList } = useSectorContext();
  const { branchListGetter, branchList } = useBranchContext();
  const { getDataCountry, dataCountry } = useSettingsContext();
  const {
    createClient,
    createClientStatus,
    createdClient,
    getAssignedClients,
    assignedClients,
    getAuthorizedManagers,
    authorizedManager,
  } = useClientContext();
  const { lang, userScope, apiErrorResponse, setApiErrorResponse } = useUserContext();
  const { createdFile, setCreatedFile, createFile, uploadProfilePicture } =
    useFileContext();
  const navigate = useNavigate();
  const { picture, tempUserId } = useProfileUploadSetup();
  const [isOpen, setIsOpen] = useState(false);
  const [isStrategicClient, setIsStrategicClient] = useState(false);
  const [isValidated, setIsValidated] = useState(false);
  const [cellHighlight, setCellHighlight] = useState(false);
  // Synchronous lock: blocks re-entrant submits before React re-renders the disabled button
  const isSubmittingRef = useRef(false);

  useEffect(() => {
    sectorListGetter();
    branchListGetter();
    getDataCountry();
  }, []);

  useEffect(() => {
    const mascaradeUserId = localStorage.getItem("connected_as");
    const userId = localStorage.getItem("id");
    const firstName = localStorage.getItem("first_name");
    const lastName = localStorage.getItem("last_name");
    const effectiveUserId =
      mascaradeUserId && mascaradeUserId !== "null"
        ? parseInt(mascaradeUserId, 10)
        : parseInt(userId, 10);
    setValue("assigned_managers", [
      {
        value: effectiveUserId,
        type: TAG_TYPES.PEOPLE,
        label: `${firstName} ${lastName}`,
      },
    ]);
  }, [setValue]);

  useEffect(() => {
    isValidated && errors && setCellHighlight(true);
  }, [errors, isValidated]);

  useEffect(() => {
    setApiErrorResponse(null);
  }, []);

  useEffect(() => {
    if (apiErrorResponse) {
      Object.entries(apiErrorResponse).forEach(([field, message]) => {
        setError(field, { type: "manual", message });
      });
    }
  }, [apiErrorResponse, setError]);

  const onSubmit = async (info) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;
    setIsOpen(true);
    try {
      await createClient(formatSentData(info));
    } catch {
      // Provider already set createClientStatus to ERROR and apiErrorResponse
    } finally {
      isSubmittingRef.current = false;
    }
  };

  const [code, setCode] = useState();
  const [status, setStatus] = useState();

  const onError = () => {
    setStatus(POPUP_STATUS.ERROR);
    setCode(FIELD_REQUIRED);
  };

  const formatData = (data) => {
    const formattedCreatedData = {
      ...data,
      sector_id: data.sector_id[0]?.value,
      strategic: isStrategicClient,
      picture_file_id: createdFile?.picture_file_id || createdFile?.uri,
      country: data?.country[0]?.value,
    };
    if (!createdFile?.picture_file_id && !createdFile?.uri) {
      onError();
      return;
    }
    onSubmit(formattedCreatedData);
  };

  const resetForm = () => {
    setIsValidated(false);
    setIsStrategicClient(false);
    reset(CLIENT_FORM_DEFAULT_VALUES);
    window.scrollTo(0, 0);
  };

  const value = useMemo(
    () => ({
      cellHighlight: cellHighlight,
      authorizedManager: authorizedManager,
      branchList: branchList,
      clientsList: assignedClients,
      getClients: ({ search }) => getAssignedClients({ search }),
      getManagers: (search) => getAuthorizedManagers({ search }),
      lang: lang,
      sectorList,
      isStrategicClient,
      setIsStrategicClient,
      createFile,
      uploadProfilePicture,
      createdFile,
      setCreatedFile,
      picture,
      setCode,
      setStatus,
      tempUserId,
      isValidated,
      dataCountry,
      apiErrorResponse,
    }),
    [
      sectorList,
      branchList,
      assignedClients,
      authorizedManager,
      cellHighlight,
      isStrategicClient,
      createFile,
      uploadProfilePicture,
      createdFile,
      setCreatedFile,
      picture,
      tempUserId,
      isValidated,
      dataCountry,
      apiErrorResponse,
    ]
  );
  return (
    <>
      <ValidationModal
        status={createClientStatus}
        messages={{
          pendingTitleId: "modal_validation_create_file",
          pendingMessageId: "modal_validation_file_pending_msg",
          successTitleId: "modal_validation_create_file",
          successMessageId: "modal_validation_file_success_msg",
          errorTitleId: "modal_validation_create_file",
          errorMessageId: "modal_validation_field_errors_msg",
          errorMessageDefault:
            "Certains champs comportent des erreurs.{br}Veuillez cliquer sur les <nowrap>icônes{errorIcon}pour</nowrap> obtenir plus de détails et corriger les informations nécessaires.",
        }}
        open={isOpen}
        onClose={() => {
          setIsOpen(false);
          if (createClientStatus === QUERY_STATUS.SUCCESS) {
            navigate(`/${userScope}/client/${createdClient?.client?.slug}`);
          }
        }}
      />
      <InfoContext.Provider value={value}>
        <AlertPopup
          code={code}
          setCode={setCode}
          setStatus={setStatus}
          status={status}
        />

        <form
          id="create-client-form"
          onSubmit={(e) => {
              clearErrors();
              handleSubmit(formatData, onError)(e);
            }}
          style={{ paddingTop: "2rem" }}
        >
          <ClientPersonalInfoForm
            setValue={(field, value) => {
              setValue(field, value);
              clearErrors(field);
            }}
            watch={watch}
            control={control}
            register={register}
            errors={errors}
          />
          <div className="form-buttons">
            <Button
              className="btn-standard btn-outline"
              onClick={resetForm}
              type="button"
              id="button-form-reset"
            >
              <FormattedMessage id="cancel" defaultMessage="Annuler" />
            </Button>
            <Button
              id="button-form-validation"
              color="btn-medium gray-700"
              type="submit"
              onClick={() => setIsValidated(true)}
              disabled={createClientStatus === QUERY_STATUS.LOADING}
              backgroundColor={
                createClientStatus === QUERY_STATUS.LOADING &&
                colors.disabledGrey
              }
            >
              <FormattedMessage
                id="button_form_validation"
                defaultMessage="Valider la création"
              />
            </Button>
          </div>
        </form>
      </InfoContext.Provider>
    </>
  );
};

export default CreateClient;
